// src/components/SetupWizard/WizardStep1_CompanyIdentity.js
//
// Phase 3, Build Step 2: Company Identity — REAL IMPLEMENTATION
//
// Creates a new client (createPlaceholderClient) or updates an existing one
// (submitClientInfoForReview). Triggers ICR research in the background.
//
// Props:
//   clientId       — string|null (null = new client, string = resume)
//   clientData     — client document from Firestore (null if new)
//   user           — Firebase auth user object (needs user.organizationId for IndustrySelect)
//   onStepComplete — callback({ clientId }) when step is done

import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Paper,
  Avatar
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { toast } from 'react-toastify';
import IndustrySelect from '../common/IndustrySelect';

const WizardStep1_CompanyIdentity = ({ clientId, clientData, user, onStepComplete }) => {
  // Pre-populate from existing client data when resuming
  const [companyName, setCompanyName] = useState(clientData?.companyName || clientData?.name || '');
  const [websiteUrl, setWebsiteUrl] = useState(clientData?.websiteUrl || '');
  const [industry, setIndustry] = useState(clientData?.industry || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Preview card state
  const [viewState, setViewState] = useState('form'); // 'form' | 'loading_preview' | 'preview' | 'preview_error'
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState('');

  // If Step 1 was already completed for this client, show summary and allow proceeding
  const isAlreadyComplete = !!clientData?.submittedForAdminReview;

  // Step 1 of 2: Validate form and fetch website preview
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!companyName.trim() || !websiteUrl.trim()) {
      setError('Company Name and Website URL are required.');
      return;
    }
    if (!websiteUrl.includes('.')) {
      setError('Please enter a valid website URL.');
      return;
    }

    setError('');
    setViewState('loading_preview');

    try {
      const fetchPreview = httpsCallable(functions, 'fetchCompanyPreview');
      const result = await fetchPreview({ url: websiteUrl.trim() });

      if (result.data?.success) {
        setPreviewData(result.data);
        setViewState('preview');
      } else {
        setPreviewError(result.data?.error || 'Could not fetch website metadata.');
        setViewState('preview_error');
      }
    } catch (err) {
      console.warn('[WizardStep1] Preview fetch failed:', err);
      setPreviewError(err.message || 'Could not reach website.');
      setViewState('preview_error');
    }
  };

  // Step 2 of 2: Actually create/update the client
  const handleConfirmAndCreate = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      let resultClientId = clientId;

      if (clientId) {
        // --- UPDATE EXISTING CLIENT ---
        const submitClientInfo = httpsCallable(functions, 'submitClientInfoForReview');
        await submitClientInfo({
          clientId,
          companyName: companyName.trim(),
          websiteUrl: websiteUrl.trim(),
          industry: industry || null
        });
        toast.success('Company information submitted successfully!');
      } else {
        // --- CREATE NEW CLIENT ---
        const createPlaceholderClient = httpsCallable(functions, 'createPlaceholderClient');
        const result = await createPlaceholderClient({
          companyName: companyName.trim(),
          websiteUrl: websiteUrl.trim(),
          industry: industry || null
        });

        resultClientId = result.data?.clientId;

        if (!resultClientId) {
          console.warn('[WizardStep1] createPlaceholderClient did not return clientId. ' +
            'Backend may need to be updated to return { clientId: docRef.id }');
          toast.success('New client created! Waiting for confirmation...');
        } else {
          toast.success('New client created successfully!');
        }
      }

      onStepComplete({ clientId: resultClientId });

    } catch (err) {
      console.error('[WizardStep1] Error submitting company info:', err);
      const errorMessage = err.message || 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
      // If we had a successful preview, return to it; otherwise stay in preview_error
      // so the "Continue Anyway" button remains visible for retry.
      setViewState(previewData ? 'preview' : 'preview_error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Return to form from preview
  const handleEditFromPreview = () => {
    setViewState('form');
    setPreviewData(null);
    setPreviewError('');
  };

  // If already completed, show a summary with option to proceed or edit
  if (isAlreadyComplete) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Company Information
        </Typography>

        <Alert severity="success" sx={{ mb: 3 }}>
          ✅ Company information already submitted for <strong>{clientData?.companyName || clientData?.name}</strong>
        </Alert>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">Company Name</Typography>
            <Typography variant="body1">{clientData?.companyName || clientData?.name || '—'}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">Website</Typography>
            <Typography variant="body1">{clientData?.websiteUrl || clientData?.submittedForAdminReview?.websiteUrl || '—'}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">Industry</Typography>
            <Typography variant="body1">{clientData?.industry || '—'}</Typography>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={() => onStepComplete({ clientId })}
            sx={{ minWidth: 180 }}
          >
            Continue →
          </Button>
        </Box>
      </Box>
    );
  }

  // ----- Loading Preview State -----
  if (viewState === 'loading_preview') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200, gap: 2 }}>
        <CircularProgress />
        <Typography variant="body1">Looking up company details...</Typography>
      </Box>
    );
  }

  // ----- Preview Card State -----
  if (viewState === 'preview' && previewData) {
    // Ensure the URL has a protocol for the link
    const fullUrl = previewData.url || websiteUrl.trim();
    const displayUrl = fullUrl.replace(/^https?:\/\//, '');

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Confirm Company
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please verify this is the correct company before we continue.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            {previewData.favicon && (
              <Avatar
                src={previewData.favicon}
                variant="rounded"
                sx={{ width: 40, height: 40, bgcolor: 'grey.100' }}
                imgProps={{ onError: (e) => { e.target.style.display = 'none'; } }}
              >
                {companyName.charAt(0).toUpperCase()}
              </Avatar>
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {companyName}
              </Typography>
              {previewData.title && previewData.title !== companyName && (
                <Typography variant="body2" color="text.secondary">
                  {previewData.title}
                </Typography>
              )}
            </Box>
          </Box>

          {previewData.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {previewData.description.length > 200
                ? previewData.description.substring(0, 200) + '...'
                : previewData.description}
            </Typography>
          )}

          {previewData.ogImage && (
            <Box
              component="img"
              src={previewData.ogImage}
              alt={`${companyName} preview`}
              onError={(e) => { e.target.style.display = 'none'; }}
              sx={{
                width: '100%',
                maxHeight: 180,
                objectFit: 'cover',
                borderRadius: 1,
                mb: 2
              }}
            />
          )}

          {industry && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Industry:</strong> {industry}
            </Typography>
          )}

          <Button
            size="small"
            endIcon={<OpenInNewIcon fontSize="small" />}
            href={fullUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ textTransform: 'none', p: 0, mt: 0.5 }}
          >
            {displayUrl}
          </Button>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            onClick={handleEditFromPreview}
            startIcon={<EditIcon />}
            disabled={isSubmitting}
            sx={{ textTransform: 'none' }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmAndCreate}
            disabled={isSubmitting}
            startIcon={isSubmitting ? null : <CheckCircleIcon />}
            sx={{ minWidth: 200 }}
          >
            {isSubmitting ? <CircularProgress size={24} /> : 'Confirm & Continue'}
          </Button>
        </Box>
      </Box>
    );
  }

  // ----- Preview Error State -----
  if (viewState === 'preview_error') {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Confirm Company
        </Typography>

         <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3 }}>
          We couldn't preview this website. Please double-check the URL is correct.
          {previewError && (
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.8 }}>
              ({previewError})
            </Typography>
          )}
        </Alert>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="body2"><strong>Company:</strong> {companyName}</Typography>
          <Typography variant="body2"><strong>URL:</strong> {websiteUrl}</Typography>
          {industry && <Typography variant="body2"><strong>Industry:</strong> {industry}</Typography>}
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            onClick={handleEditFromPreview}
            startIcon={<EditIcon />}
            sx={{ textTransform: 'none' }}
          >
            Edit URL
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmAndCreate}
            disabled={isSubmitting}
            sx={{ minWidth: 200 }}
          >
            {isSubmitting ? <CircularProgress size={24} /> : 'Continue Anyway'}
          </Button>
        </Box>
      </Box>
    );
  }

  // ----- Form State (default) -----
  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" gutterBottom>
        Company Information
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Provide your client's company details. This information is used to generate initial research and calibrate AI insights.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Company Name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            fullWidth
            required
            disabled={isSubmitting}
            placeholder="e.g., Acme Corporation"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Website URL"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            fullWidth
            required
            disabled={isSubmitting}
            placeholder="e.g., www.acmecorp.com"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <IndustrySelect
            value={industry}
            onChange={setIndustry}
            organizationId={user?.organizationId}
            disabled={isSubmitting}
            helperText="Select the primary industry for this client"
            allowOther={true}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          sx={{ minWidth: 180 }}
        >
          {isSubmitting ? <CircularProgress size={24} /> : 'Continue →'}
        </Button>
      </Box>
    </Box>
  );
};

export default WizardStep1_CompanyIdentity;